import React from 'react';

/**
 * Componente que renderiza a barra de progresso visual dos status das pendências.
 * @param {object} props - Propriedades do componente.
 * @param {Array} props.data - Array de objetos, cada um representando um status.
 * @param {number} props.totalPendencies - O número total de pendências.
 * @returns {React.Component|null} A barra de status ou nulo.
 */
const PendencyStatusBar = ({ data, totalPendencies }) => {
    if (!data || totalPendencies === 0) return null;

    return (
        <div className="h-3 w-full rounded-full overflow-hidden flex bg-slate-200 dark:bg-slate-700">
            {data.map(item => {
                if (item.value === 0) return null;
                const widthPercentage = (item.value / totalPendencies) * 100;
                return (
                    <div
                        key={item.name}
                        className="h-full"
                        style={{
                            width: `${widthPercentage}%`,
                            backgroundColor: item.color,
                        }}
                        title={`${item.name}: ${item.percentage.toFixed(1)}%`}
                    />
                );
            })}
        </div>
    );
};

/**
 * Componente que renderiza a legenda para a barra de status das pendências.
 * @param {object} props - Propriedades do componente.
 * @param {Array} props.data - Array de objetos, cada um representando um status.
 * @returns {React.Component|null} A legenda ou nulo.
 */
const PendencyStatusLegend = ({ data }) => {
    if (!data || data.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
            {data.filter(item => item.value > 0).map(item => (
                <div key={item.name} className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }}></span>
                    <span className="text-slate-600 dark:text-slate-400 font-medium">{item.name}:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{item.percentage.toFixed(1)}%</span>
                </div>
            ))}
        </div>
    );
};

/**
 * Componente principal que agrupa a barra de status e a legenda das pendências.
 * @param {object} props - Propriedades do componente.
 * @param {Array} props.data - Array de objetos com os dados de status.
 * @returns {React.Component} O componente completo de exibição de status.
 */
const PendencyStatusDisplay = React.memo(({ data }) => {
    // --- CORREÇÃO APLICADA AQUI ---
    // Adicionamos uma verificação para garantir que 'data' não é undefined antes de usar 'reduce'.
    // Se 'data' ainda não chegou, consideramos o total como 0.
    const totalPendencies = data ? data.reduce((acc, item) => acc + item.value, 0) : 0;
    
    if (totalPendencies === 0) {
        return <p className="text-sm text-center text-slate-500 dark:text-slate-400 py-2">Nenhuma pendência para exibir o status.</p>;
    }

    return (
        <div className="w-full">
            <PendencyStatusBar data={data} totalPendencies={totalPendencies}/>
            <PendencyStatusLegend data={data} />
        </div>
    );
});

export default PendencyStatusDisplay;
